<template>
  <form
    id="customer-code-container"
    autocomplete="on"
    @submit.prevent="submitCustomerCode"
  >
    <p><b>Enter your customer code</b></p>

    <label for="customer_code" class="sr-only">Customer Code</label>

    <input
      id="customer_code"
      v-model="customerCode"
      name="customer_code"
      aria-label="Customer Code"
      type="text"
      autocomplete="on"
      autocapitalize="none"
      spellcheck="false"
      inputmode="text"
      style="text-align: center"
      autofocus
      @input="onCustomerCodeInput"
      @keyup.enter="submitCustomerCode"
    />

    <button type="button" @click="submitCustomerCode">Proceed to Login</button>
  </form>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import useAlertBannerStore from "@/store/AlertBanner";
import { useAPI } from "@/api";
import Analytics from "@/api/analytics";

const { updateActive, updateMessage, updateVariant } = useAlertBannerStore();
const API = useAPI();

const emit = defineEmits<{
  (e: "customer-code", value: string): void;
}>();

const customerCode = ref<string>("");

function warn(message: string) {
  updateVariant("warning");
  updateMessage(message);
  updateActive(true);
}

function normalizeCustomerCode(raw: string): {
  value: string;
  warning?: string;
} {
  let value = raw.toLowerCase();
  let warning: string | undefined;

  if (/^-+/.test(value)) {
    warning ??= "Dash '-' is not permitted as the first character";
    value = value.replace(/^-+/, "");
  }

  if (/--+/.test(value)) {
    warning ??= "Double dash '--' is not permitted";
    value = value.replace(/--+/g, "-");
  }

  if (value.length > 36) {
    warning ??= "Only 36 characters are allowed";
    value = value.slice(0, 36);
  }

  return { value, warning };
}

function onCustomerCodeInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const { value, warning } = normalizeCustomerCode(input.value);

  if (warning) {
    warn(warning);
  }

  if (input.value !== value) {
    input.value = value;
  }

  customerCode.value = value;
}

async function submitCustomerCode() {
  if (!customerCode.value) {
    warn("Empty code");
    return;
  }

  if (customerCode.value.endsWith("-")) {
    warn("Dash '-' is not permitted as the last character");
    customerCode.value = customerCode.value.slice(0, -1);
    return;
  }

  try {
    const resStatus = await API.CustomerCode.checkCustomerCode(
      customerCode.value,
    );

    if (resStatus === 200) {
      emit("customer-code", customerCode.value);
      return;
    }

    warn("Invalid code");
    customerCode.value = "";
  } catch (error: unknown) {
    warn("Invalid code");
    customerCode.value = "";
  }
}

onMounted(() => {
  Analytics.pageView("redirect_uri", "Customer Code Screen");
});
</script>

<style scoped lang="scss">
@import "./../assets/scss/index.scss";

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

#customer-code-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  p {
    margin-bottom: $margin-s;
  }

  input {
    margin-bottom: $margin-m;
  }
}
</style>
